import { AnyRobotSummaryCardProps } from "../robots/AnyRobotSummaryCard";
import { buildPermission } from "portal/utils/auth";
import { FeatureFlag, useFeatureFlag } from "portal/utils/hooks/useFeatureFlag";
import {
  getAlarmsStatus,
  getAlmanacStatus,
  getBandingStatus,
  getCropLibraryStatus,
  getCropStatus,
  getCustomerStatus,
  getDiscriminatorStatus,
  getImplementationStatus,
  getJobStatus,
  getLaserStatus,
  getLifetimeStatus,
  getLocaltimeStatus,
  getLocationStatus,
  getModelinatorStatus,
  getP2PStatus,
  getPlantProfileStatus,
  getThinningStatus,
  getTodayStatus,
  getVersionStatus,
  getWeedingStatus,
} from "portal/components/robots/RobotStatus";
import { getColumns, InfoColumnId } from "portal/utils/fleetViews";
import { getConfigNode } from "portal/utils/configs";
import { getCrops } from "portal/utils/crops";
import {
  isRobotClassifyingByCustomCatCompatPlantProfile,
  isRobotClassifyingByPlantProfile,
} from "portal/utils/categoryCollection/plantProfile";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalEndpoints } from "portal/state/portalApi";
import { portalLanguageOrDefault } from "portal/i18nConstants";
import { RobotSummaryCard } from "../robots/RobotSummaryCard";
import { SummaryCardItemProps } from "portal/components/SummaryCardItem";
import { useAuthorizationRequired } from "../auth/WithAuthorizationRequired";
import { useCanSeeOtherCustomers } from "portal/views/customers/permissions";
import { useHeadlineInfo } from "portal/views/laserweeder/LaserWeederSummary";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { useJigsawMetrics } from "portal/utils/hooks/useJigsawMetrics";
import { useLazyPopups, useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useMemoAsync } from "portal/utils/hooks/useMemoAsync";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { withErrorBoundary } from "portal/components/ErrorBoundary";
import React, { FunctionComponent, useMemo } from "react";

const _LaserWeederSummaryCard: FunctionComponent<AnyRobotSummaryCardProps> = ({
  link = false,
  serial,
  variant,
  summary: inputData,
  columns,
}) => {
  const { t, i18n } = useTranslation();
  const language = portalLanguageOrDefault(i18n.language);
  const { user, measurementSystem } = useSelf();
  const useJigsaw = useJigsawMetrics();
  const { isEnabled: includePlantProfile } = useFeatureFlag(
    FeatureFlag.CATEGORY_COLLECTION
  );

  // only load config if we're on screen
  const [ref, intersection] = useIntersectionObserver();
  const isOnScreen = intersection?.isIntersecting ?? false;

  const [getRobot] = useLazyPopups(portalEndpoints.getRobot.useLazyQuery());
  const [summary] = useMemoAsync(
    async () => {
      if (inputData) {
        return inputData;
      }
      const { data } = await getRobot(
        {
          includeDailyMetrics: true,
          language: i18n.language,
          serial,
          useJigsaw,
        },
        true
      );
      return data;
    },
    [getRobot, i18n.language, inputData, serial, useJigsaw],
    inputData
  );

  const [getConfig] = useLazyPopups(portalEndpoints.getConfig.useLazyQuery());
  const [config] = useMemoAsync(
    async () => {
      if (inputData?.config) {
        return getConfigNode(inputData.config);
      }
      if (
        !isOnScreen ||
        !columns ||
        !columns.includes(InfoColumnId.LASERS_OFFLINE)
      ) {
        return;
      }
      const { data } = await getConfig({ serial }, true);
      return data?.config ? getConfigNode(data.config) : undefined;
    },
    [columns, getConfig, inputData?.config, isOnScreen, serial],
    inputData?.config
  );
  const headlineInfo = useHeadlineInfo(summary);

  const { data: robotVeselkaCrops } = useQueryPopups(
    portalEndpoints.listCrops.useQuery()
  );

  const veselkaCrops = useMemo(
    () =>
      getCrops(summary?.robot?.serial, config, robotVeselkaCrops, language, t),
    [config, robotVeselkaCrops, language, summary?.robot?.serial, t]
  );

  const robotCropDisplayName = summary?.jigsawCrop?.localizedName;

  const weedingStatus = useMemo(
    () => getWeedingStatus(t, summary, robotCropDisplayName),
    [summary, t, robotCropDisplayName]
  );
  const thinningStatus = useMemo(
    () => getThinningStatus(t, summary),
    [summary, t]
  );
  const bandingStatus = useMemo(
    () => getBandingStatus(t, summary),
    [summary, t]
  );

  const plantProfileStatus = useMemo(
    () => getPlantProfileStatus(t, i18n, summary),
    [t, i18n, summary]
  );

  const jobStatus = useMemo(() => getJobStatus(t, summary, true), [summary, t]);
  const alarmStatus = useMemo(
    () => getAlarmsStatus(t, summary, true),
    [summary, t]
  );

  const canReadHardware = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.hardware,
        PermissionDomain.all
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.hardware,
        PermissionDomain.customer
      ),
    ],
  });
  const versionStatus = useMemo(
    () => getVersionStatus(t, summary, canReadHardware),
    [canReadHardware, summary, t]
  );

  const todayStatus = useMemo(
    () => getTodayStatus(t, i18n, summary),
    [i18n, summary, t]
  );
  const cropStatus = useMemo(
    () => getCropStatus(t, summary, true),
    [summary, t]
  );
  const cropLibraryStatus = useMemo(
    () => getCropLibraryStatus(t, summary, veselkaCrops, true),
    [veselkaCrops, summary, t]
  );

  const canReadAlmanacs = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.almanacs,
        PermissionDomain.all
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.almanacs,
        PermissionDomain.customer
      ),
    ],
  });
  const almanacStatus = useMemo(
    () => getAlmanacStatus(t, summary, canReadAlmanacs),
    [canReadAlmanacs, summary, t]
  );

  const canReadDiscriminators = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.discriminators,
        PermissionDomain.all
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.discriminators,
        PermissionDomain.customer
      ),
    ],
  });
  const discriminatorStatus = useMemo(
    () => getDiscriminatorStatus(t, summary, canReadDiscriminators),
    [summary, t, canReadDiscriminators]
  );

  const canReadModelsAdvanced = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.models_advanced,
        PermissionDomain.all
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.models_advanced,
        PermissionDomain.customer
      ),
    ],
  });
  const modelinatorStatus = useMemo(
    () => getModelinatorStatus(t, summary, canReadModelsAdvanced),
    [summary, t, canReadModelsAdvanced]
  );

  const canReadLasers = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.lasers,
        PermissionDomain.all
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.lasers,
        PermissionDomain.customer
      ),
    ],
  });
  const laserStatus = getLaserStatus(
    t,
    serial,
    config,
    summary,
    variant === "sidebar", // don't display disabled laser summary matrix on shortened views
    canReadLasers
  );
  const locationStatus = useMemo(
    () => getLocationStatus(t, summary),
    [summary, t]
  );
  const p2pStatus = useMemo(() => getP2PStatus(t, summary), [summary, t]);
  const lifetimeStatus = useMemo(
    () => getLifetimeStatus(t, i18n, measurementSystem, summary),
    [i18n, measurementSystem, summary, t]
  );
  const loadingStatus: SummaryCardItemProps = useMemo(
    () => ({
      icon: (
        <div className="w-6 text-center">
          {/* for some reason this is causing fatal performance issues */}
          {/* <CircularProgress size="1.2rem" className="text-white" /> */}
        </div>
      ),
      text: (
        <span className="text-gray-500">
          {t("components.Loading.placeholder")}
        </span>
      ),
    }),
    [t]
  );

  const canUpdateRobots = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.robots,
      PermissionDomain.all
    )
  );
  const implementationStatus = getImplementationStatus(
    summary,
    link || !canUpdateRobots
  );
  const localtimeStatus = getLocaltimeStatus(t, i18n, summary);

  const canReadCustomers = useCanSeeOtherCustomers();
  const canUpdateCustomers = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.customers,
      PermissionDomain.all
    )
  );
  const customerStatus = getCustomerStatus(
    summary,
    canReadCustomers,
    !canUpdateCustomers
  );

  const priorityStatus = useMemo(() => {
    const result: SummaryCardItemProps[] = [];
    if (columns && summary && user) {
      const allColumns = getColumns(
        i18n,
        t,
        measurementSystem,
        useJigsaw,
        includePlantProfile === true
      );
      for (const columnId of columns) {
        const column = allColumns.find((column) => column.id === columnId);
        if (column?.renderForCard) {
          const status = column.renderForCard(
            summary,
            config,
            robotCropDisplayName
          );
          if (status) {
            result.push(status);
          }
        }
      }
    } else {
      if (customerStatus) {
        result.push(customerStatus);
      }
      result.push(
        implementationStatus,
        localtimeStatus,
        todayStatus,
        alarmStatus
      );

      // conditional lasers
      if (!summary?.lasers) {
        result.push(loadingStatus);
      } else if (summary.lasers.enabledLasers < summary.lasers.maxLasers) {
        result.push(laserStatus);
      }

      // conditional version
      const softwareVersion = summary?.robot?.health?.softwareVersion;
      const targetVersion = summary?.robot?.health?.targetVersion;
      if (
        !softwareVersion ||
        (targetVersion && targetVersion !== softwareVersion)
      ) {
        result.push(versionStatus);
      }

      result.push(lifetimeStatus);
    }
    return result;
  }, [
    alarmStatus,
    columns,
    config,
    customerStatus,
    i18n,
    implementationStatus,
    includePlantProfile,
    laserStatus,
    lifetimeStatus,
    loadingStatus,
    localtimeStatus,
    measurementSystem,
    robotCropDisplayName,
    summary,
    t,
    todayStatus,
    useJigsaw,
    user,
    versionStatus,
  ]);

  const summarySections = useMemo(() => {
    const plantProfilesEnabled =
      includePlantProfile && isRobotClassifyingByPlantProfile(summary);

    const field: SummaryCardItemProps[] = [
      cropStatus,
      weedingStatus,
      thinningStatus,
      jobStatus,
      bandingStatus,
    ];
    if (plantProfilesEnabled) {
      field.push(plantProfileStatus);
    }

    const software: SummaryCardItemProps[] = [
      versionStatus,
      cropLibraryStatus,
      modelinatorStatus,
    ];
    if (!isRobotClassifyingByCustomCatCompatPlantProfile(summary)) {
      software.push(almanacStatus, discriminatorStatus);
    }

    const hardware: SummaryCardItemProps[] = [
      implementationStatus,
      localtimeStatus,
      alarmStatus,
      laserStatus,
      locationStatus,
      lifetimeStatus,
    ];

    const management: SummaryCardItemProps[] = [todayStatus];

    if (canReadCustomers) {
      if (customerStatus) {
        management.push(customerStatus);
      }
      software.push(p2pStatus);
    }

    const result: {
      label: string;
      items: SummaryCardItemProps[];
    }[] = [
      {
        label: t("utils.metrics.groups.field"),
        items: field,
      },
      {
        label: t("views.fleet.robots.summary.sections.software"),
        items: software,
      },
      {
        label: t("views.fleet.robots.hardware.title"),
        items: hardware,
      },
      {
        label: t("views.fleet.robots.summary.sections.management"),
        items: management,
      },
    ];

    return result;
  }, [
    summary,
    alarmStatus,
    almanacStatus,
    bandingStatus,
    cropLibraryStatus,
    cropStatus,
    customerStatus,
    discriminatorStatus,
    implementationStatus,
    includePlantProfile,
    canReadCustomers,
    jobStatus,
    laserStatus,
    lifetimeStatus,
    localtimeStatus,
    locationStatus,
    modelinatorStatus,
    p2pStatus,
    plantProfileStatus,
    t,
    thinningStatus,
    todayStatus,
    versionStatus,
    weedingStatus,
  ]);

  return (
    <RobotSummaryCard
      ref={ref}
      variant={variant}
      priorityStatus={priorityStatus}
      summarySections={summarySections}
      serial={serial}
      customerSerial={summary?.robot?.customerSerial}
      headlineInfo={headlineInfo}
    />
  );
};

export const LaserWeederSummaryCard = withErrorBoundary(
  { i18nKey: "views.fleet.robots.summary.failed" },
  _LaserWeederSummaryCard
);
